/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Retracked from 'js/app/retracked';
import { stringKeyToTuple } from 'js/lib/stringKeyTuple';

import { Link, Typography2, breakpoints } from '@coursera/cds-core';
import type { LinkProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import type { SkillSetResult } from 'bundles/enterprise-learner-search/components/skillsetSearch/SkillSetSearchDataProvider';
import { DEFAULT_SKILLSET_LOGO } from 'bundles/enterprise-learner-search/components/skillsetSearch/SkillSetSearchDataProvider';
import { toProgramSkillSet } from 'bundles/enterprise-legacy-learner-home/links';

const TrackedLink = Retracked.withVisibilityTracking(withSingleTracked({ type: 'BUTTON' })<LinkProps>(Link));

type Props = {
  skillset: SkillSetResult;
};

const useStyles = () => {
  return {
    card: css`
      min-height: 56px;
      max-width: 370px;
      display: flex !important;
      align-items: center;
      padding: var(--cds-spacing-100) var(--cds-spacing-200) !important;
      background: var(--cds-color-purple-25);
      border: 1px solid var(--cds-color-grey-50);
      border-radius: 4px;
      cursor: pointer;
      margin-top: var(--cds-spacing-100);
      margin-bottom: var(--cds-spacing-100);
      transition: background 0.1s !important;

      ${breakpoints.down('sm')} {
        max-width: unset;
      }

      :hover {
        background: var(--cds-color-purple-50);
      }
    `,
    cardTextArea: css`
      margin-left: var(--cds-spacing-150);
      display: flex;
      flex-direction: column;
    `,
    title: css`
      text-decoration: underline;
    `,
  };
};

const SkillSetCard = ({ skillset }: Props) => {
  const styles = useStyles();

  const [, suggestionID] = stringKeyToTuple(skillset.objectID);
  const [programSlug, skillsetSlug] = suggestionID.split('/');

  const trackingData = {
    programSlug,
    skillsetSlug,
    skillsetName: skillset.name,
    objectId: `skillset~${programSlug}/${skillsetSlug}`,
  };

  return (
    <TrackedLink
      {...Link.defaultProps}
      trackingName="skillset_card_search_result"
      trackingData={trackingData}
      data={trackingData}
      css={styles.card}
      tabIndex={0}
      href={toProgramSkillSet(programSlug, skillsetSlug)}
    >
      <img src={DEFAULT_SKILLSET_LOGO} alt="" />
      <div css={styles.cardTextArea}>
        <Typography2 color="highlightBlue" css={styles.title} variant="subtitleMedium" component="span">
          {skillset.name}
        </Typography2>
      </div>
    </TrackedLink>
  );
};

export default SkillSetCard;
